// Isometric Simulation Types
// Modern 3D-style office visualization

// ============================================
// COORDINATE SYSTEMS
// ============================================

export interface IsoCoord {
  x: number; // Isometric X (grid position)
  y: number; // Isometric Y (grid position)
  z?: number; // Height/elevation
}

export interface ScreenCoord {
  x: number; // Screen X (pixels)
  y: number; // Screen Y (pixels)
}

export interface TileCoord {
  col: number; // Column in grid
  row: number; // Row in grid
}

// ============================================
// ISOMETRIC CONFIGURATION
// ============================================

export const ISO_CONFIG = {
  // Tile dimensions (2:1 ratio for isometric)
  TILE_WIDTH: 64,
  TILE_HEIGHT: 32,

  // Grid size
  GRID_COLS: 20,
  GRID_ROWS: 16,

  // Canvas size
  CANVAS_WIDTH: 1400,
  CANVAS_HEIGHT: 900,

  // Elevation
  FLOOR_HEIGHT: 0,
  DESK_HEIGHT: 16,
  AGENT_HEIGHT: 48,

  // Animation
  MOVE_SPEED: 120, // pixels per second
  ANIMATION_FPS: 60,
} as const;

// ============================================
// TIME OF DAY
// ============================================

export type TimeOfDay = 'DAWN' | 'MORNING' | 'MIDDAY' | 'AFTERNOON' | 'DUSK' | 'EVENING' | 'NIGHT';

export interface DayLighting {
  timeOfDay: TimeOfDay;
  ambientColor: string;
  shadowColor: string;
  shadowOpacity: number;
  lightDirection: number; // degrees
  skyGradient: [string, string];
  windowGlow: string;
}

export const TIME_LIGHTING: Record<TimeOfDay, DayLighting> = {
  DAWN: {
    timeOfDay: 'DAWN',
    ambientColor: 'rgba(255, 200, 150, 0.3)',
    shadowColor: 'rgba(100, 80, 60, 0.3)',
    shadowOpacity: 0.2,
    lightDirection: -30,
    skyGradient: ['#FFD89B', '#8EC5FC'],
    windowGlow: 'rgba(255, 200, 100, 0.4)',
  },
  MORNING: {
    timeOfDay: 'MORNING',
    ambientColor: 'rgba(255, 255, 220, 0.2)',
    shadowColor: 'rgba(80, 80, 100, 0.25)',
    shadowOpacity: 0.25,
    lightDirection: -45,
    skyGradient: ['#a8edea', '#fed6e3'],
    windowGlow: 'rgba(255, 255, 200, 0.3)',
  },
  MIDDAY: {
    timeOfDay: 'MIDDAY',
    ambientColor: 'rgba(255, 255, 255, 0.15)',
    shadowColor: 'rgba(60, 60, 80, 0.3)',
    shadowOpacity: 0.3,
    lightDirection: 0,
    skyGradient: ['#00d2ff', '#3a7bd5'],
    windowGlow: 'rgba(255, 255, 255, 0.2)',
  },
  AFTERNOON: {
    timeOfDay: 'AFTERNOON',
    ambientColor: 'rgba(255, 240, 200, 0.2)',
    shadowColor: 'rgba(80, 70, 60, 0.3)',
    shadowOpacity: 0.35,
    lightDirection: 45,
    skyGradient: ['#ffecd2', '#fcb69f'],
    windowGlow: 'rgba(255, 220, 150, 0.3)',
  },
  DUSK: {
    timeOfDay: 'DUSK',
    ambientColor: 'rgba(255, 150, 100, 0.3)',
    shadowColor: 'rgba(60, 40, 60, 0.4)',
    shadowOpacity: 0.4,
    lightDirection: 60,
    skyGradient: ['#f093fb', '#f5576c'],
    windowGlow: 'rgba(255, 150, 80, 0.5)',
  },
  EVENING: {
    timeOfDay: 'EVENING',
    ambientColor: 'rgba(100, 100, 150, 0.3)',
    shadowColor: 'rgba(30, 30, 50, 0.5)',
    shadowOpacity: 0.5,
    lightDirection: 90,
    skyGradient: ['#4834d4', '#686de0'],
    windowGlow: 'rgba(255, 200, 100, 0.6)',
  },
  NIGHT: {
    timeOfDay: 'NIGHT',
    ambientColor: 'rgba(50, 50, 80, 0.4)',
    shadowColor: 'rgba(20, 20, 40, 0.6)',
    shadowOpacity: 0.6,
    lightDirection: 0,
    skyGradient: ['#0f0c29', '#302b63'],
    windowGlow: 'rgba(200, 200, 255, 0.3)',
  },
};

// ============================================
// OFFICE LAYOUT
// ============================================

export type IsoTileType =
  | 'FLOOR'
  | 'CARPET'
  | 'WALL_N'
  | 'WALL_E'
  | 'WALL_S'
  | 'WALL_W'
  | 'WALL_CORNER_NE'
  | 'WALL_CORNER_NW'
  | 'WALL_CORNER_SE'
  | 'WALL_CORNER_SW'
  | 'DOOR'
  | 'WINDOW'
  | 'GLASS_WALL';

export type IsoFurnitureType =
  | 'DESK_MODERN'
  | 'DESK_STANDING'
  | 'CHAIR_OFFICE'
  | 'CHAIR_MEETING'
  | 'MONITOR'
  | 'LAPTOP'
  | 'COFFEE_MACHINE'
  | 'WATER_COOLER'
  | 'TABLE_MEETING'
  | 'TABLE_LUNCH'
  | 'SOFA'
  | 'PLANT_LARGE'
  | 'PLANT_SMALL'
  | 'BOOKSHELF'
  | 'FILING_CABINET'
  | 'WHITEBOARD'
  | 'PRINTER'
  | 'SERVER_RACK'
  | 'SCREEN_LARGE' // For KPI display
  | 'SCREEN_KPI' // KPI dashboard screen
  | 'SCREEN_ALARM' // Alarm status screen
  | 'SCREEN_RECOMMEND'; // Recommendations screen

export interface IsoTile {
  type: IsoTileType;
  elevation: number;
  variant?: number;
}

export interface IsoFurniture {
  id: string;
  type: IsoFurnitureType;
  position: IsoCoord;
  rotation: 0 | 90 | 180 | 270;
  zIndex?: number;
}

export interface IsoRoom {
  id: string;
  name: string;
  type: 'WORKSPACE' | 'MEETING' | 'BREAK' | 'SERVER' | 'ENTRANCE' | 'CORRIDOR';
  bounds: {
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
  };
  floorType: IsoTileType;
  furniture: IsoFurniture[];
}

export interface IsoOfficeLayout {
  width: number;
  height: number;
  rooms: IsoRoom[];
  tiles: IsoTile[][];
  furniture: IsoFurniture[];
  agentSpawnPoints: IsoCoord[];
  kpiScreenPosition: IsoCoord;
}

// ============================================
// AGENT VISUALIZATION
// ============================================

export type IsoAgentRole = 'COORDINATOR' | 'DELEGATOR' | 'WORKER';

export interface IsoAgentStyle {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  glowColor: string;
}

export const AGENT_STYLES: Record<IsoAgentRole, IsoAgentStyle> = {
  COORDINATOR: {
    primaryColor: '#3B82F6', // Blue
    secondaryColor: '#1E40AF',
    accentColor: '#DBEAFE',
    glowColor: 'rgba(59, 130, 246, 0.4)',
  },
  DELEGATOR: {
    primaryColor: '#10B981', // Green
    secondaryColor: '#065F46',
    accentColor: '#D1FAE5',
    glowColor: 'rgba(16, 185, 129, 0.4)',
  },
  WORKER: {
    primaryColor: '#F59E0B', // Amber
    secondaryColor: '#92400E',
    accentColor: '#FEF3C7',
    glowColor: 'rgba(245, 158, 11, 0.4)',
  },
};

export type IsoAgentActivity =
  | 'IDLE'
  | 'WALKING'
  | 'WORKING'
  | 'TYPING'
  | 'THINKING'
  | 'TALKING'
  | 'DRINKING_COFFEE'
  | 'MEETING'
  | 'PRESENTING'
  | 'LEAVING';

export interface IsoAgent {
  id: string;
  name: string;
  role: IsoAgentRole;
  position: IsoCoord;
  targetPosition?: IsoCoord;
  activity: IsoAgentActivity;
  activityProgress?: number;
  currentTask?: string;
  isBusy: boolean;
  style: IsoAgentStyle;
}

// ============================================
// KPI VISUALIZATION
// ============================================

export interface IsoKPIDisplay {
  id: string;
  name: string;
  value: number;
  target: number;
  unit: string;
  trend: 'UP' | 'DOWN' | 'STABLE';
  status: 'EXCELLENT' | 'GOOD' | 'WARNING' | 'CRITICAL';
  alarmActive: boolean;
}

// ============================================
// SIMULATION STATE
// ============================================

export interface IsoSimulationState {
  isRunning: boolean;
  isPaused: boolean;
  speed: 1 | 2 | 4 | 8;
  currentTime: Date;
  timeOfDay: TimeOfDay;
  lighting: DayLighting;
  layout: IsoOfficeLayout;
  agents: IsoAgent[];
  kpis: IsoKPIDisplay[];
  activeAlarms: string[];
  showMorningModal: boolean;
  showEveningModal: boolean;
}

// ============================================
// CHECKPOINT SYSTEM (24-hour operation)
// ============================================

export type CheckpointType = 'MORNING_APPROVAL' | 'EVENING_FEEDBACK';

export type CheckpointStatus = 'PENDING' | 'RESPONDED' | 'AUTO_PROCEEDED' | 'SKIPPED';

export interface DailyCheckpoint {
  id: string;
  type: CheckpointType;
  scheduledTime: Date;
  expiresAt: Date;
  status: CheckpointStatus;
  response?: {
    approved?: boolean;
    comments?: string;
    rating?: 1 | 2 | 3 | 4 | 5;
    feedback?: string;
    adjustments?: string[];
  };
  autoProceededAt?: Date;
  autoProceededReason?: string;
}

export interface MorningApprovalData {
  todaysPlan: {
    id: string;
    title: string;
    description: string;
    actions: {
      id: string;
      title: string;
      priority: string;
      estimatedDuration: number;
    }[];
  };
  yesterdaySummary?: string;
  carryoverTasks: string[];
  suggestedPriorities: string[];
}

export interface EveningDebriefData {
  accomplishments: {
    title: string;
    status: 'COMPLETED' | 'PARTIAL' | 'BLOCKED';
  }[];
  metricsChanges: {
    name: string;
    previousValue: number;
    currentValue: number;
    change: number;
  }[];
  challenges: string[];
  tomorrowPreview: string;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Convert isometric grid coordinates to screen coordinates
 */
export function isoToScreen(isoX: number, isoY: number, originX: number = 0, originY: number = 0): ScreenCoord {
  const { TILE_WIDTH, TILE_HEIGHT } = ISO_CONFIG;
  return {
    x: originX + (isoX - isoY) * (TILE_WIDTH / 2),
    y: originY + (isoX + isoY) * (TILE_HEIGHT / 2),
  };
}

/**
 * Convert screen coordinates to isometric grid coordinates
 */
export function screenToIso(screenX: number, screenY: number, originX: number = 0, originY: number = 0): IsoCoord {
  const { TILE_WIDTH, TILE_HEIGHT } = ISO_CONFIG;
  const relX = screenX - originX;
  const relY = screenY - originY;
  return {
    x: (relX / (TILE_WIDTH / 2) + relY / (TILE_HEIGHT / 2)) / 2,
    y: (relY / (TILE_HEIGHT / 2) - relX / (TILE_WIDTH / 2)) / 2,
  };
}

/**
 * Calculate the correct render order (depth sorting) for isometric objects
 */
export function calculateDepth(isoX: number, isoY: number, isoZ: number = 0): number {
  return isoX + isoY + isoZ * 0.1;
}

/**
 * Get time of day based on current hour
 */
export function getTimeOfDay(hour: number): TimeOfDay {
  if (hour >= 5 && hour < 7) return 'DAWN';
  if (hour >= 7 && hour < 11) return 'MORNING';
  if (hour >= 11 && hour < 14) return 'MIDDAY';
  if (hour >= 14 && hour < 17) return 'AFTERNOON';
  if (hour >= 17 && hour < 19) return 'DUSK';
  if (hour >= 19 && hour < 22) return 'EVENING';
  return 'NIGHT';
}

/**
 * Get lighting configuration for current time
 */
export function getLightingForTime(time: Date): DayLighting {
  const hour = time.getHours();
  const timeOfDay = getTimeOfDay(hour);
  return TIME_LIGHTING[timeOfDay];
}

/**
 * Linear interpolation between two values
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * Math.max(0, Math.min(1, t));
}

/**
 * Calculate path between two isometric points (A* simplified)
 */
export function calculatePath(from: IsoCoord, to: IsoCoord): IsoCoord[] {
  // Simple direct path for now - can be enhanced with A*
  const path: IsoCoord[] = [];
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const steps = Math.max(Math.abs(dx), Math.abs(dy));

  for (let i = 0; i <= steps; i++) {
    path.push({
      x: from.x + (dx * i) / steps,
      y: from.y + (dy * i) / steps,
    });
  }

  return path;
}
